import PropTypes from 'prop-types';
import { BsBuilding, BsCheckCircle, BsCpu, BsDashCircle, BsFunnel, BsGeoAlt, BsSearch } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

import { contactModeLabel } from '../../constants/conversation';

const fmt = (value) => (value ?? 0).toLocaleString('de-DE');

const Section = ({ title, children }) => (
	<div>
		<div className="mb-1 text-xs text-bgBase-700">{title}</div>
		{children}
	</div>
);

Section.propTypes = { title: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

const Line = ({ icon: Icon, children, muted = false }) => (
	<div className={`flex items-start gap-1.5 ${muted ? 'text-bgBase-700 line-through' : 'text-text'}`}>
		<Icon className={`mt-1 h-3 w-3 flex-shrink-0 ${muted ? 'text-bgBase-600' : 'text-bgBase-700'}`} />
		<span>{children}</span>
	</div>
);

Line.propTypes = { icon: PropTypes.elementType.isRequired, children: PropTypes.node.isRequired, muted: PropTypes.bool };

/**
 * What the assistant understood, the sources, the scope and the cost ceiling; hand-off to the
 * search mask at the bottom.
 */
const ProfileRail = ({ profile, mandate, estimate, onOpenForm }) => (
	<aside className="hidden w-[280px] flex-shrink-0 flex-col border-l border-bgBase-500 bg-bgBase-300 lg:flex">
		<div className="flex items-center gap-2 border-b border-bgBase-500 px-4 py-3">
			<BsFunnel className="h-4 w-4 text-bgBase-800" />
			<span className="text-xs-head">Suchprofil</span>
		</div>
		<div className="flex-1 space-y-4 overflow-y-auto p-4 text-sm">
			<Section title="Suchbegriffe">
				{profile?.terms?.length ? (
					profile.terms.map((term) => (
						<Line key={term} icon={BsSearch}>
							{term}
						</Line>
					))
				) : (
					<span className="text-bgBase-700">–</span>
				)}
			</Section>
			<Section title="Standort">
				{profile?.locations?.length ? (
					profile.locations.map((location) => (
						<Line
							key={`${location.type}-${location.name}`}
							icon={location.type === 'bundesland' ? BsBuilding : BsGeoAlt}
						>
							{location.name}
							{location.type === 'city' && profile.radius ? ` · ${profile.radius} km` : ''}
						</Line>
					))
				) : (
					<span className="text-bgBase-700">offen</span>
				)}
			</Section>
			{profile?.employeesFrom && (
				<Section title="Filter (kostenlos)">
					<Line icon={BsFunnel}>≥ {profile.employeesFrom} Mitarbeiter</Line>
				</Section>
			)}
			<Section title="Kriterien (KI prüft je Firma, kostenlos)">
				{profile?.criteria?.length ? (
					profile.criteria.map((criterion) => (
						<Line key={criterion} icon={BsCpu}>
							{criterion}
						</Line>
					))
				) : (
					<span className="text-bgBase-700">keine</span>
				)}
			</Section>
			<Section title="Quellen">
				<Line icon={BsCheckCircle}>Firmenpool (Handelsregister, Websites)</Line>
				<Line icon={mandate?.mapsEnabled ? BsCheckCircle : BsDashCircle} muted={!mandate?.mapsEnabled}>
					Google Maps
				</Line>
			</Section>
			{mandate && (
				<Section title="Umfang">
					<div>max. {mandate.companyLimit} Firmen</div>
					<div>{contactModeLabel(mandate.contacts?.mode)}</div>
					{mandate.contacts?.mode !== 'none' && (
						<div className="text-xs text-bgBase-800">
							E-Mail{mandate.contactData?.includes('phone') ? ' + Telefon' : ''} + Postadresse
						</div>
					)}
				</Section>
			)}
		</div>
		<div className="space-y-2 border-t border-bgBase-500 p-3">
			<div className="flex items-center justify-between text-xs text-bgBase-800">
				<span>Obergrenze</span>
				<span className="font-semibold tabular-nums text-text">bis {fmt(estimate?.total)} Credits</span>
			</div>
			<Button variant="outline" size="sm" fullWidth onClick={onOpenForm}>
				In Suchmaske öffnen
			</Button>
		</div>
	</aside>
);

ProfileRail.propTypes = {
	profile: PropTypes.object,
	mandate: PropTypes.object,
	estimate: PropTypes.object,
	onOpenForm: PropTypes.func.isRequired,
};

export default ProfileRail;
